import React, { useMemo, memo, useState, useEffect } from 'react';
import { Feature } from '../data/featureData';

// This is now used correctly in the mobile animation logic below.
const MOBILE_ANNOTATION_Y_VH = -70; // Feel free to tune this value

interface SingleFeatureDisplayProps {
  feature: Feature;
  sectionIndex: number;
  isActiveSection: boolean;
  scrollY: number;
  viewportHeight: number;
  sectionStartVhValues: number[];
  isInteractionReady: boolean;
}

const SingleFeatureDisplay: React.FC<SingleFeatureDisplayProps> = ({
    feature, sectionIndex, isActiveSection, scrollY, viewportHeight, sectionStartVhValues, isInteractionReady
}) => {
    const isMobile = useMemo(() => window.innerWidth < 768, []);

    const [animationState, setAnimationState] = useState<'hidden' | 'entering' | 'idle' | 'exiting'>('hidden');

    useEffect(() => {
        if (isMobile) {
            if (isActiveSection) {
                setAnimationState('entering');
                const timer = setTimeout(() => setAnimationState('idle'), 800);
                return () => clearTimeout(timer);
            } else {
                if (animationState === 'idle' || animationState === 'entering') {
                    setAnimationState('exiting');
                }
            }
        }
    }, [isActiveSection, isMobile, animationState]);

    // --- Animation Logic ---
    let imageOpacity: number;
    let textBoxOpacity: number;
    let textBoxTranslateY: number;
    let imageSpecificFilter = 'none';

    if (isMobile) {
        const state = animationState;
        imageOpacity = (state === 'entering' || state === 'idle') ? 1 : 0;
        textBoxOpacity = (state === 'entering' || state === 'idle') ? 1 : 0;

        if (state === 'entering' || state === 'idle') {
            textBoxTranslateY = 0; // At rest, no translation needed
        } else if (state === 'exiting') {
            textBoxTranslateY = -200; // Fly off top
        } else {
            textBoxTranslateY = 100; // Start position below the screen
        }
    } else {
        // <<< FIX: Re-added useMemo hook for performance >>>
        const progress = useMemo(() => {
            if (!isInteractionReady || viewportHeight <= 0) return 0;
            const startVh = sectionStartVhValues[sectionIndex] || 0;
            const sStartPx = (startVh / 100) * viewportHeight;
            const sHeightPx = (150 / 100) * viewportHeight;
            if (sHeightPx <= 0) return 0;
            const scrollInSection = scrollY - sStartPx;
            return Math.max(0, Math.min(1, scrollInSection / sHeightPx));
        }, [isInteractionReady, scrollY, viewportHeight, sectionIndex, sectionStartVhValues]);
        
        const IMG_FADE_IN_END = 0.5;
        const TEXT_APPEAR_AND_TRAVEL_START = 0.15;
        const TEXT_SCROLL_OFF_END = 0.95;
        const TOTAL_TEXT_TRAVEL_VH = -115;
        
        imageOpacity = progress < IMG_FADE_IN_END ? (progress / IMG_FADE_IN_END) : 1;
        if (progress > TEXT_SCROLL_OFF_END) {
            imageOpacity = 1 - (progress - TEXT_SCROLL_OFF_END) / (1 - TEXT_SCROLL_OFF_END);
        }

        if (progress >= TEXT_APPEAR_AND_TRAVEL_START && progress < TEXT_SCROLL_OFF_END) {
            textBoxOpacity = 1.0;
            const travelProgress = (progress - TEXT_APPEAR_AND_TRAVEL_START) / (TEXT_SCROLL_OFF_END - TEXT_APPEAR_AND_TRAVEL_START);
            textBoxTranslateY = TOTAL_TEXT_TRAVEL_VH * travelProgress;
        } else {
            textBoxOpacity = 0.0;
            textBoxTranslateY = progress >= TEXT_SCROLL_OFF_END ? TOTAL_TEXT_TRAVEL_VH : 0;
        }
    }
    
    const { responsiveImageWidth, responsiveAnnotationWidth, imageToDisplay } = useMemo(() => ({
        responsiveImageWidth: isMobile ? '90vw' : '880px',
        responsiveAnnotationWidth: isMobile ? '90vw' : '505px',
        imageToDisplay: isMobile ? feature.imageSrcMobile : feature.imageSrcDesktop,
    }), [isMobile, feature]);
    
    const containerStyle: React.CSSProperties = {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        opacity: imageOpacity, pointerEvents: 'none', zIndex: isActiveSection ? 15 : 10,
        transition: 'opacity 0.8s ease-in-out',
    };
    
    const featureContentWrapperStyle: React.CSSProperties = {
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
    };

    const imageStyle: React.CSSProperties = {
        maxWidth: responsiveImageWidth, maxHeight: '60vh',
        width: 'auto', height: 'auto', objectFit: 'contain',
        filter: imageSpecificFilter, 
        marginTop: '20px', // <<< CHANGED from marginBottom
    };

    const annotationBoxStyle: React.CSSProperties = {
        width: responsiveAnnotationWidth, maxWidth: '90%', padding: '20px 24px',
        fontFamily: "'Inter', sans-serif", fontWeight: 300, fontSize: '20px', lineHeight: '1.4',
        color: '#1A1A1A', textAlign: 'left',
        backgroundColor: 'rgba(255, 255, 255, 0.97)', borderRadius: '5px',
        border: '1px solid #b3b3b3', boxShadow: '0 0 10px rgba(132, 132, 132, 0.25)',
        opacity: textBoxOpacity,
        transform: isMobile ? `translateY(${textBoxTranslateY}px)` : 'none',
        transition: 'transform 0.8s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.6s ease-in-out',
    };

    if (animationState === 'hidden' && !isActiveSection) return null;

    return (
        <div style={containerStyle}>
            <div style={featureContentWrapperStyle}>
                {/* 👇 The annotation box now comes BEFORE the image 👇 */}
                <div style={annotationBoxStyle}>
                    {feature.annotationText}
                </div>
                <img src={imageToDisplay} alt={feature.imageAltText} style={imageStyle} />
            </div>
        </div>
    );
};

export default memo(SingleFeatureDisplay);